import React from "react";
import MultiSelect, { MultiSelectProps } from "@/components/atoms/MultiSelect";
import { useTranslation } from "react-i18next";
import { enumToStringArray } from "@/utils/enumToArray";
import OrderStatusEnum from "@/constants/enums/OrderStatusEnum";

/**
 * Statuses that are not offered as filter options. This is a UI-only trim: orders in these states
 * are still returned by the API and still render their own status label in the table. Remove a
 * status from this list to bring its option back.
 */
export const HIDDEN_ORDER_STATUSES: string[] = [
  OrderStatusEnum.AWAITING_WEEKLY_INVOICING,
  OrderStatusEnum.AWAITING_MONTHLY_INVOICING,
  OrderStatusEnum.AWAITING_QUARTERLY_INVOICING,
];

/**
 * MultiSelectOrderStatus component renders a MultiSelect dropdown for Order Statuses.
 * It automatically populates options from OrderStatusEnum and handles translations.
 */
export const MultiSelectOrderStatus: React.FC<MultiSelectProps> = ({ ...restProps }: MultiSelectProps) => {
  const { t, i18n } = useTranslation();

  const orderStatusList = enumToStringArray(OrderStatusEnum);

  const options = orderStatusList
    .filter((entry) => !HIDDEN_ORDER_STATUSES.includes(entry))
    .map((entry) => ({
      value: entry.toString(),
      label: t(`enumerables:orderStatus.${entry}`),
    }))
    // Sorted on the translated label, not the enum key, so the order matches what the user reads.
    .sort((a, b) => a.label.localeCompare(b.label, i18n.language));

  return (
    <MultiSelect
      label={t("orders:orderStatus")}
      data-testid="multi-select-order-status"
      data={options}
      {...restProps}
    />
  );
};

export default MultiSelectOrderStatus;
